import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Loading, Empty, PageTitle } from "@/components/States";
import { inputCls, btnPrimary, btnOutline, btnGhost, card, label } from "@/lib/ui";

const useFetch = (path) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(path).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [path]);
  useEffect(() => {
    load();
  }, [load]);
  return [data, load];
};

const act = async (path, body, msg, reload) => {
  try {
    await api.post(path, body);
    toast.success(msg);
    reload();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Metrics = () => {
  const [m] = useFetch("/admin/metrics");
  if (!m) return <Loading />;
  const rows = [["Listings", m.listings], ["Active listings", m.active_listings], ["Offers", m.offers], ["Confirmed appointments", m.confirmed_appointments], ["Completed sales", m.completed_sales], ["Completion rate", m.completion_rate == null ? "—" : `${m.completion_rate}%`], ["Cancellations", m.cancellations], ["Late cancellations", m.late_cancellations], ["Returning sellers", m.returning_sellers], ["Users", m.users], ["Open reports", m.open_reports], ["Open disputes/appeals", m.open_disputes]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {rows.map(([k, v]) => <div key={k} data-testid={`metric-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={card}><div className="text-2xl font-extrabold font-mono">{v}</div><div className="text-xs text-slate-500 font-semibold">{k}</div></div>)}
    </div>
  );
};

const Reports = () => {
  const [rs, load] = useFetch("/admin/reports");
  const [notes, setNotes] = useState({});
  if (!rs) return <Loading />;
  if (!rs.length) return <Empty title="No reports" />;
  return (
    <div className="space-y-3">
      {rs.map((r) => (
        <div key={r.id} data-testid={`admin-report-${r.id}`} className={card + " space-y-2"}>
          <div className="flex justify-between text-sm"><b>{r.target_type === "listing" ? `Listing: ${r.target?.title}` : `User: ${r.target?.first_name}`}</b><span className="text-xs font-bold uppercase text-blue-700">{r.status}</span></div>
          <p className="text-sm">{r.reason}{r.details ? ` — ${r.details}` : ""}</p>
          <p className="text-xs text-slate-500">By {r.reporter?.first_name} · {fmtDateTime(r.created_at)}</p>
          {r.status === "open" && (
            <>
              <input data-testid={`admin-report-notes-${r.id}`} placeholder="Decision notes (audit log)" value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} className={inputCls} />
              <div className="flex flex-wrap gap-2">
                <button data-testid={`admin-report-dismiss-${r.id}`} className={btnOutline + " h-9"} onClick={() => act(`/admin/reports/${r.id}/action`, { action: "dismiss", notes: notes[r.id] || "" }, "Dismissed", load)}>Dismiss</button>
                <button data-testid={`admin-report-warn-${r.id}`} className={btnOutline + " h-9"} onClick={() => act(`/admin/reports/${r.id}/action`, { action: "warn_user", notes: notes[r.id] || "" }, "User warned", load)}>Warn user</button>
                {r.target_type === "listing" && <button data-testid={`admin-report-remove-${r.id}`} className={btnOutline + " h-9"} onClick={() => act(`/admin/reports/${r.id}/action`, { action: "remove_listing", notes: notes[r.id] || "" }, "Listing removed", load)}>Remove listing</button>}
                <button data-testid={`admin-report-suspend-${r.id}`} className={btnGhost + " text-red-600"} onClick={() => act(`/admin/reports/${r.id}/action`, { action: "suspend_user", notes: notes[r.id] || "" }, "User suspended", load)}>Suspend user</button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const Disputes = () => {
  const [ds, load] = useFetch("/admin/disputes");
  const [notes, setNotes] = useState({});
  if (!ds) return <Loading />;
  if (!ds.length) return <Empty title="No disputes" />;
  return (
    <div className="space-y-3">
      {ds.map((d) => (
        <div key={d.id} data-testid={`admin-dispute-${d.id}`} className={card + " space-y-2"}>
          <div className="flex justify-between text-sm"><b>{d.reporter?.first_name} reported {d.accused?.first_name} as no-show</b><span data-testid={`admin-dispute-status-${d.id}`} className="text-xs font-bold uppercase text-blue-700">{d.status.replace(/_/g, " ")}</span></div>
          {d.appointment?.confirmed_terms && <p className="text-xs text-slate-500">Pickup: {fmtDateTime(d.appointment.confirmed_terms.start_at)} at {d.appointment.confirmed_terms.location}</p>}
          <p className="text-sm"><b>Reporter:</b> {d.statement || "—"}</p>
          <p className="text-sm"><b>Accused response:</b> {d.response || <i className="text-slate-500">No response yet (respond by {fmtDateTime(d.respond_by)})</i>}</p>
          {d.appeal && <p className="text-sm"><b>Appeal ({d.appeal.status}):</b> {d.appeal.text}</p>}
          {(d.status === "open" || d.appeal?.status === "pending") && (
            <>
              <input data-testid={`admin-dispute-notes-${d.id}`} placeholder="Decision notes (audit log)" value={notes[d.id] || ""} onChange={(e) => setNotes({ ...notes, [d.id]: e.target.value })} className={inputCls} />
              <div className="flex flex-wrap gap-2">
                {d.status === "open" ? (
                  <>
                    <button data-testid={`admin-dispute-substantiate-${d.id}`} className={btnPrimary + " h-9"} onClick={() => act(`/admin/disputes/${d.id}/decide`, { decision: "substantiated", notes: notes[d.id] || "" }, "Marked substantiated", load)}>Substantiate</button>
                    <button data-testid={`admin-dispute-reject-${d.id}`} className={btnOutline + " h-9"} onClick={() => act(`/admin/disputes/${d.id}/decide`, { decision: "not_substantiated", notes: notes[d.id] || "" }, "Marked not substantiated", load)}>Not substantiated</button>
                  </>
                ) : (
                  <>
                    <button data-testid={`admin-appeal-uphold-${d.id}`} className={btnOutline + " h-9"} onClick={() => act(`/admin/disputes/${d.id}/appeal`, { decision: "upheld", notes: notes[d.id] || "" }, "Decision upheld", load)}>Uphold decision</button>
                    <button data-testid={`admin-appeal-overturn-${d.id}`} className={btnPrimary + " h-9"} onClick={() => act(`/admin/disputes/${d.id}/appeal`, { decision: "overturned", notes: notes[d.id] || "" }, "Decision overturned", load)}>Overturn</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const Users = () => {
  const [q, setQ] = useState("");
  const [us, load] = useFetch(`/admin/users?q=${encodeURIComponent(q)}`);
  const [days, setDays] = useState({});
  return (
    <div className="space-y-3">
      <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className={inputCls} />
      {!us ? <Loading /> : us.map((u) => (
        <div key={u.id} data-testid={`admin-user-${u.id}`} className={card + " flex flex-wrap items-center gap-3"}>
          <div className="flex-1 min-w-[180px]"><p className="font-semibold text-sm">{u.first_name} {u.role === "admin" && "(admin)"} {u.is_demo && "(demo)"}</p><p className="text-xs text-slate-500">{u.email} · {u.status} · warnings {u.warnings}{u.booking_restricted_until ? ` · restricted until ${u.booking_restricted_until.slice(0, 10)}` : ""}</p></div>
          {u.role !== "admin" && (
            <div className="flex gap-2 items-center">
              <input data-testid={`admin-restrict-days-${u.id}`} type="number" min="0" placeholder="days" value={days[u.id] ?? ""} onChange={(e) => setDays({ ...days, [u.id]: e.target.value })} className={inputCls + " w-20 h-9"} />
              <button data-testid={`admin-restrict-btn-${u.id}`} className={btnOutline + " h-9 whitespace-nowrap"} onClick={() => act(`/admin/users/${u.id}/restrict`, { days: Number(days[u.id] || 0), notes: "Manual admin action" }, Number(days[u.id] || 0) ? "Booking restriction set" : "Restriction lifted", load)}>{Number(days[u.id] || 0) ? "Restrict bookings" : "Lift restriction"}</button>
              <button data-testid={`admin-status-btn-${u.id}`} className={btnGhost + (u.status === "suspended" ? "" : " text-red-600")} onClick={() => act(`/admin/users/${u.id}/status`, { action: u.status === "suspended" ? "active" : "suspended", notes: "" }, "Status updated", load)}>{u.status === "suspended" ? "Reinstate" : "Suspend"}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RulesEditor = () => {
  const [r, load] = useFetch("/rules");
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (r) setForm(r);
  }, [r]);
  if (!form) return <Loading />;
  const save = async () => {
    try {
      await api.put("/admin/rules", form);
      toast.success("Rules updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className={card + " space-y-3"}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(form).map(([k, v]) => (
          <div key={k}><span className={label}>{k.replace(/_/g, " ")}</span><input data-testid={`rule-input-${k}`} type="number" min="0" value={v} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} className={inputCls} /></div>
        ))}
      </div>
      <button data-testid="rules-save-btn" onClick={save} className={btnPrimary}>Save rules</button>
    </div>
  );
};

const Audit = () => {
  const [logs] = useFetch("/admin/audit");
  if (!logs) return <Loading />;
  if (!logs.length) return <Empty title="No decisions yet" />;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
      {logs.map((l) => (
        <div key={l.id} data-testid={`audit-${l.id}`} className="p-3 text-sm flex flex-wrap gap-x-3">
          <span className="font-mono text-xs text-slate-500">{fmtDateTime(l.created_at)}</span><b>{l.action}</b><span className="text-slate-600">{l.target_type}:{l.target_id.slice(0, 8)}</span><span className="text-slate-500">by {l.admin?.first_name}</span>{l.notes && <span className="text-slate-700">— {l.notes}</span>}
        </div>
      ))}
    </div>
  );
};

const EmailLog = () => {
  const [d, load] = useFetch("/admin/email-log");
  if (!d) return <Loading />;
  return (
    <div className="space-y-3">
      <div className={card + " flex flex-wrap items-center gap-3"}>
        <p className="text-sm flex-1">Email provider: <b data-testid="email-configured">{d.configured ? "configured" : "not configured"}</b></p>
        <button data-testid="email-test-btn" className={btnOutline + " h-9"} onClick={() => act("/admin/email-test", {}, "Test email attempted — see the log below", load)}>Send test email to me</button>
      </div>
      {d.logs.length === 0 ? <Empty title="No emails yet" /> : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {d.logs.map((l) => (
            <div key={l.id} data-testid={`email-log-${l.id}`} className="p-3 text-sm flex flex-wrap gap-x-3">
              <span className="font-mono text-xs text-slate-500">{fmtDateTime(l.created_at)}</span><b className={l.status === "failed" ? "text-red-600" : ""}>{l.status}</b><span>{l.kind}</span>{l.error && <span className="text-slate-500">— {l.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  return (
    <div>
      <PageTitle sub="Reports, disputes, restrictions and an audit trail of every decision.">Trust & safety</PageTitle>
      <Tabs defaultValue="metrics">
        <TabsList className="mb-4 flex-wrap h-auto">
          {["metrics", "reports", "disputes", "users", "rules", "email", "audit"].map((t) => <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize">{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="metrics"><Metrics /></TabsContent>
        <TabsContent value="reports"><Reports /></TabsContent>
        <TabsContent value="disputes"><Disputes /></TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="rules"><RulesEditor /></TabsContent>
        <TabsContent value="email"><EmailLog /></TabsContent>
        <TabsContent value="audit"><Audit /></TabsContent>
      </Tabs>
    </div>
  );
}
